/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  Alert,
  Divider,
  FormCheckbox,
  FormEmailField,
  FormIsoCountryCodeField,
  FormPhoneCountryCodeField,
  FormPhoneField,
  FormTextField,
  SubmitButton,
  Typography,
} from 'omakase';
import { Form, Formik, FormikHelpers } from 'formik';
import { Grid } from 'ui';
import { SmsAlertStopped } from 'components/sms/SmsAlertStopped';
import { SmsSubscribeConfirmationModal } from 'components/modal/SmsSubscribeConfirmationModal';
import { core } from '@tock/coretypes';
import { guestImageUrl } from '@tock/images';
import { isEmail, requireFields } from '@tock/shared/utils/FormValidators';
import { isSmsPhoneStatusTypeOptIn, isSmsPhoneStatusTypeOptOutHard } from 'utils/smsUtils';
import { requiresEmailConfirmation } from '@tock/shared/utils/patron';
import { useFeatureFlagForConsumer } from 'featureFlags/useConsumerFeatureFlag';
import { useHasDirectBrandUpdateFlags } from 'hooks/useHasDirectBrandUpdateFlags';
import React, { useCallback, useEffect, useRef, useState } from 'react';

interface Props {
  detectedLocation?: core.DetectedLocation;
  patron: core.IPatron;
  lastGuest?: core.IPatron;
  isLoading: boolean;
  onUpdateProfile(formValues: any, newImageFile?: any, orientation?: number): any;
  patronProfile?: core.PatronProfile;
}

interface Fields {
  company: string;
  email: string;
  firstName: string;
  isoCountryCode: string;
  jobTitle: string;
  lastName: string;
  phone: string;
  phoneCountryCode: string;
  zipCode: string;
  patronConsentsToSms?: boolean;
  smsPhoneConsentStatus: core.SmsPhone.StatusType;
}

const validate = (data: Fields) => {
  const errors = requireFields(data, 'firstName', 'lastName', 'email', 'phone');

  if (!isEmail(data.email)) {
    errors.email = 'Please enter a valid email address.';
  }

  return errors;
};

const getPhoneValidationKey = (phone: string, phoneCountryCode: string) =>
  `${phoneCountryCode}|${phone.trim()}`;

const EditProfileForm: React.FunctionComponent<Props> = ({
  patronProfile,
  lastGuest,
  detectedLocation,
  onUpdateProfile,
}) => {
  const [isSmsConfirmationModalOpen, setIsSmsConfirmationModalOpen] = useState(false);
  const profile = patronProfile;
  const patron = profile?.patron;
  const formikRef = useRef<any>(null);
  const hasTwilioDirectBrandUpdatesFlag = useHasDirectBrandUpdateFlags();
  const disablePhoneChanges =
    useFeatureFlagForConsumer('JCE_BRIDGE_DISABLE_CONSUMER_ACCOUNT_EDIT') &&
    !!patronProfile?.hasGdusAccount;

  const newGuest = {
    ...patronProfile,
    ...patron,
    ...lastGuest,
  };
  const initialPhoneValidationKey = getPhoneValidationKey(
    newGuest?.phone ?? '',
    newGuest?.phoneCountryCode ?? detectedLocation?.phoneCountryCode ?? ''
  );
  const [lastValidatedPhoneKey, setLastValidatedPhoneKey] = useState(initialPhoneValidationKey);

  useEffect(() => {
    setLastValidatedPhoneKey(initialPhoneValidationKey);
  }, [initialPhoneValidationKey]);

  const [phoneConsentStatus, setPhoneConsentStatus] = useState<
    core.SmsPhone.StatusType | undefined
  >(newGuest?.smsPhoneConsentStatus ?? undefined);

  const imageLink = patronProfile?.patron?.imageUrl;
  const imageStyle = {
    backgroundImage: imageLink ? `url(${guestImageUrl(imageLink, 400)})` : '',
  };

  const handleSubmit = async (values: Fields, helper: FormikHelpers<Fields>) => {
    helper.setStatus('');

    try {
      onUpdateProfile(values);
    } catch (msg) {
      helper.setStatus(msg);
    }
  };

  const showSmsConfirmationModal = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (formikRef.current) {
      if (formikRef.current.values.patronConsentsToSms === false) {
        // Prevent the checkbox from toggling when the user is attempting to check it.
        // We only intercept the action when the current Formik value is false
        // (i.e., the user is trying to enable SMS). For unchecking, allow default behavior.
        e.preventDefault();

        // Show the modal so the user can choose to opt-in.
        setIsSmsConfirmationModalOpen(true);
      } else {
        // If they are unchecking the box, we can allow that without confirmation and just update Formik values accordingly.
        const optOutStatus: core.SmsPhone.StatusType = 'OPT_OUT_SOFT';
        updateSmsConsentFields(optOutStatus);
      }
    }
  };

  const updateSmsConsentFields = useCallback((status: core.SmsPhone.StatusType | undefined) => {
    if (formikRef.current) {
      formikRef.current.setFieldValue('smsPhoneConsentStatus', status); // api value
      formikRef.current.setFieldValue('patronConsentsToSms', isSmsPhoneStatusTypeOptIn(status)); // checkbox
    }
  }, []);

  const handleSmsConfirmationModalResult = (status: core.SmsPhone.StatusType) => {
    updateSmsConsentFields(status);
    setIsSmsConfirmationModalOpen(false);
  };

  const updatePhoneStatus = useCallback(
    (status: core.SmsPhone.StatusType | undefined) => {
      setPhoneConsentStatus(status);
      updateSmsConsentFields(status);
    },
    [updateSmsConsentFields]
  );

  const handlePhoneValidationComplete = useCallback(
    ({
      phone,
      phoneCountryCode,
    }: {
      phone: string;
      phoneCountryCode: string;
      status: core.SmsPhone.StatusType | undefined;
    }) => {
      const validatedPhoneKey = getPhoneValidationKey(phone, phoneCountryCode);
      const currentValues = formikRef.current?.values;
      if (!currentValues) {
        return;
      }

      const currentPhoneKey = getPhoneValidationKey(
        currentValues.phone ?? '',
        currentValues.phoneCountryCode ?? ''
      );
      if (validatedPhoneKey === currentPhoneKey) {
        setLastValidatedPhoneKey(validatedPhoneKey);
      }
    },
    []
  );

  return (
    <>
      <Formik<Fields>
        innerRef={formikRef}
        initialValues={{
          company: newGuest.company ?? '',
          email: newGuest?.email ?? '',
          firstName: newGuest?.firstName ?? '',
          isoCountryCode: newGuest?.isoCountryCode ?? detectedLocation?.isoCountryCode ?? '',
          jobTitle: newGuest?.jobTitle ?? '',
          lastName: newGuest?.lastName ?? '',
          phone: newGuest?.phone ?? '',
          phoneCountryCode: newGuest?.phoneCountryCode ?? detectedLocation?.phoneCountryCode ?? '',
          zipCode: newGuest?.zipCode ?? '',
          patronConsentsToSms: isSmsPhoneStatusTypeOptIn(newGuest?.smsPhoneConsentStatus),
          smsPhoneConsentStatus: newGuest?.smsPhoneConsentStatus ?? 'NOT_SPECIFIED',
        }}
        onSubmit={handleSubmit}
        validate={validate}
        enableReinitialize
      >
        {({ isValid, isSubmitting, dirty, status, values }) => {
          const isPhonePendingValidation =
            getPhoneValidationKey(values.phone ?? '', values.phoneCountryCode ?? '') !==
            lastValidatedPhoneKey;
          const disableButton = !isValid || isSubmitting || isPhonePendingValidation || !dirty;

          return (
            <Form>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <div className="Account-profileHeader">
                    <div className="Account-imageContainer">
                      <div className="Account-image" style={imageStyle} />
                    </div>
                  </div>
                </Grid>
                {status && (
                  <Grid item xs={12}>
                    <Alert severity="error">{status}</Alert>
                  </Grid>
                )}
                <Grid item xs={12} sm={6}>
                  <FormTextField
                    name="firstName"
                    autoComplete="given-name"
                    label="First name"
                    variant="inverse"
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormTextField
                    name="lastName"
                    autoComplete="family-name"
                    label="Last name"
                    variant="inverse"
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <FormEmailField
                    id="profileEmail"
                    name="email"
                    disabled={
                      !!patronProfile?.patron && !requiresEmailConfirmation(patronProfile.patron)
                    }
                    label="Email address"
                    variant="inverse"
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <FormPhoneCountryCodeField
                    variant="inverse"
                    fullWidth
                    disabled={disablePhoneChanges}
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <FormPhoneField
                    variant="inverse"
                    fullWidth
                    disabled={disablePhoneChanges}
                    onValidatePhone={updatePhoneStatus}
                    onPhoneValidationComplete={handlePhoneValidationComplete}
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <FormIsoCountryCodeField variant="inverse" fullWidth />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormTextField
                    name="zipCode"
                    label="ZIP/Postal code"
                    variant="inverse"
                    fullWidth
                  />
                </Grid>
                {hasTwilioDirectBrandUpdatesFlag && (
                  <Grid item xs={12} sm={12}>
                    {isSmsPhoneStatusTypeOptOutHard(phoneConsentStatus) ? (
                      <SmsAlertStopped severity="warning" />
                    ) : (
                      <FormCheckbox
                        name="patronConsentsToSms"
                        label="Receive text confirmations and updates from Tock."
                        value="true"
                        weight="book"
                        size="small"
                        onChange={showSmsConfirmationModal}
                      />
                    )}
                  </Grid>
                )}
                <Grid item xs={12}>
                  <Divider mt={2} mb={2} />
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="heading04">Work information</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormTextField
                    name="company"
                    autoComplete="organization"
                    label="Company"
                    variant="inverse"
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormTextField
                    name="jobTitle"
                    autoComplete="organization-title"
                    label="Job Title"
                    variant="inverse"
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12}>
                  <SubmitButton disabled={disableButton} variant="primary" size="large">
                    Save changes
                  </SubmitButton>
                </Grid>
              </Grid>
            </Form>
          );
        }}
      </Formik>
      {hasTwilioDirectBrandUpdatesFlag && (
        <SmsSubscribeConfirmationModal
          curPatronHardUnsubscribed={isSmsPhoneStatusTypeOptOutHard(phoneConsentStatus)}
          onClose={handleSmsConfirmationModalResult}
          isOpen={isSmsConfirmationModalOpen}
          pageType="Patron account"
        />
      )}
    </>
  );
};

export default EditProfileForm;
